import type { LucideIcon } from 'lucide-react';

interface EmptyStateCardProps {
  icon: LucideIcon;
  title: string;
  description: string;
  action?: { label: string; onClick: () => void };
  compact?: boolean;
}

/**
 * Componente reutilizável de Empty State para cards de dashboard.
 * Exibe ícone, título amigável, descrição e CTA opcional.
 * Fontes fixas em px para garantir consistência entre todos os cards.
 */
export function EmptyStateCard({ icon: Icon, title, description, action, compact }: EmptyStateCardProps) {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: compact ? '14px 12px' : '28px 24px',
      textAlign: 'center',
      gap: 6,
      ...(compact ? {} : { minHeight: 160 }),
    }}>
      <div style={{
        width: 36,
        height: 36,
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #ede9fe 0%, #e0e7ff 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        animation: 'emptyStatePulse 3s ease-in-out infinite',
        flexShrink: 0,
      }}>
        <Icon size={18} style={{ color: '#818cf8' }} />
      </div>
      <div>
        <h4 style={{
          fontSize: 13,
          fontWeight: 700,
          color: 'var(--text-main, #1e293b)',
          margin: '0 0 3px 0',
          lineHeight: 1.3,
        }}>
          {title}
        </h4>
        <p style={{
          fontSize: 11.5,
          color: 'var(--text-muted, #94a3b8)',
          margin: 0,
          maxWidth: 260,
          lineHeight: 1.45,
        }}>
          {description}
        </p>
      </div>
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          style={{
            marginTop: 2,
            padding: '6px 16px',
            borderRadius: 8,
            border: 'none',
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            color: 'white',
            fontSize: 12,
            fontWeight: 700,
            cursor: 'pointer',
            transition: 'all .2s ease',
            boxShadow: '0 2px 8px rgba(99, 102, 241, .25)',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow = '0 4px 16px rgba(99, 102, 241, .35)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.transform = '';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(99, 102, 241, .25)';
          }}
        >
          {action.label}
        </button>
      )}
    </div>
  );
}
